import { reportFormPresence } from './formMapPresence';
import { hostWebApp } from './hostWebApp';

export type LocationSource =
  | 'browser-initial'
  | 'browser-refined'
  | 'browser-watch'
  | 'hostWebApp'
  | 'last-known';

export interface LocationFix {
  lat: number;
  lng: number;
  accuracy: number | null;
  timestamp: number;
  source: LocationSource;
}

export interface HighAccuracyOptions {
  desiredAccuracyMeters?: number;
  initialTimeoutMs?: number;
  refinementTimeoutMs?: number;
  allowLastKnownFallback?: boolean;
  maxLastKnownAgeMs?: number;
  allowHostLocationFallback?: boolean;
}

export interface HighAccuracyResult {
  fix: LocationFix | null;
  fulfilled: boolean;
  usedFallback: boolean;
  reason?: string;
  attempts: LocationSource[];
}

let lastFix: LocationFix | null = null;

const DEFAULT_DESIRED_ACCURACY = 25;
const DEFAULT_INITIAL_TIMEOUT = 15000;
const DEFAULT_REFINEMENT_TIMEOUT = 6000;
const DEFAULT_MAX_LAST_KNOWN_AGE = 60000;
/** Skip refine watch when Wi‑Fi/IP accuracy will never tighten to pad-grade. */
const COARSE_SKIP_REFINE_METERS = 250;
const LOW_ACCURACY_FALLBACK_TIMEOUT_MS = 20000;
const LOW_ACCURACY_MAX_AGE_MS = 60000;

export type GeolocationFailureCode = 1 | 2 | 3 | 0;

export function formatGeolocationFailure(error: GeolocationPositionError | { code?: number; message?: string } | null | undefined): string {
  const code = typeof error?.code === 'number' ? error.code : 0;
  const base = (error?.message || '').trim();
  if (code === 1) {
    return base || 'User denied Geolocation';
  }
  if (code === 2) {
    return base ? `Geolocation unavailable: ${base}` : 'Geolocation position unavailable';
  }
  if (code === 3) {
    return base ? `Geolocation timeout: ${base}` : 'Geolocation timeout';
  }
  return base || 'Geolocation error';
}

/** True only for hard permission blocks — not timeouts or unavailable fixes. */
export function isGeolocationPermissionDeniedReason(reason: unknown): boolean {
  if (reason && typeof reason === 'object' && 'code' in (reason as object)) {
    const code = Number((reason as { code?: number }).code);
    if (code === 1) return true;
    if (code === 2 || code === 3) return false;
  }
  const text = typeof reason === 'string'
    ? reason
    : reason instanceof Error
      ? reason.message
      : '';
  const lower = text.toLowerCase();
  if (!lower) return false;
  // Timeouts / unavailable must never be treated as permanent deny.
  if (lower.includes('timeout') || lower.includes('unavailable') || lower.includes('timed out')) {
    return false;
  }
  return (
    lower.includes('user denied')
    || lower.includes('permission denied')
    || lower.includes('notallowed')
    || /\bdenied\b/.test(lower)
  );
}


function toFix(position: GeolocationPosition, source: LocationSource): LocationFix {
  return {
    lat: position.coords.latitude,
    lng: position.coords.longitude,
    accuracy:
      typeof position.coords.accuracy === 'number'
        ? Math.max(position.coords.accuracy, 0)
        : null,
    timestamp: position.timestamp,
    source,
  };
}

function recordFix(fix: LocationFix) {
  // Telegram/host location is IP civic POI (often the town police station).
  // Never cache it as "last known GPS" or form presence.
  if (fix.source === 'hostWebApp') return;
  lastFix = fix;
  if (fix.source.startsWith('browser')) void reportFormPresence(fix);
}

export function getLastKnownFix(maxAgeMs: number = DEFAULT_MAX_LAST_KNOWN_AGE): LocationFix | null {
  if (!lastFix || lastFix.source === 'hostWebApp') {
    return null;
  }
  const age = Date.now() - lastFix.timestamp;
  if (age > maxAgeMs) {
    return null;
  }
  return lastFix;
}

function readGeolocationOnce(options: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        const err = new Error(formatGeolocationFailure(error)) as Error & { code?: number };
        err.code = typeof error?.code === 'number' ? error.code : 0;
        reject(err);
      },
      options,
    );
  });
}

async function attemptBrowserLocation(
  desiredAccuracy: number,
  initialTimeout: number,
  refinementTimeout: number
): Promise<HighAccuracyResult> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return {
      fix: null,
      fulfilled: false,
      usedFallback: false,
      reason: 'Geolocation API unavailable',
      attempts: [],
    };
  }

  // Safari (and all browsers) require a secure context for geolocation.
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return {
      fix: null,
      fulfilled: false,
      usedFallback: false,
      reason: 'Geolocation requires HTTPS (secure context)',
      attempts: [],
    };
  }

  const attempts: LocationSource[] = [];
  let lastReason: string | undefined;

  // Ladder tuned for Safari desktop/mobile browsers: high-accuracy + maximumAge:0
  // often times out on Wi‑Fi, while Telegram/native WebViews succeed quickly.
  // Two steps keep the Allow dialog responsive: fresh high-accuracy first,
  // then one warm low-accuracy fallback (Safari desktop Wi‑Fi often needs this).
  const ladder: Array<{
    enableHighAccuracy: boolean;
    timeout: number;
    maximumAge: number;
    tag: string;
  }> = [
    {
      enableHighAccuracy: true,
      timeout: initialTimeout,
      maximumAge: 0,
      tag: 'high-fresh',
    },
    {
      enableHighAccuracy: false,
      timeout: Math.max(initialTimeout, LOW_ACCURACY_FALLBACK_TIMEOUT_MS),
      maximumAge: LOW_ACCURACY_MAX_AGE_MS,
      tag: 'low-warm',
    },
  ];

  let position: GeolocationPosition | null = null;
  let source: LocationSource = 'browser-initial';

  for (const step of ladder) {
    try {
      position = await readGeolocationOnce({
        enableHighAccuracy: step.enableHighAccuracy,
        timeout: step.timeout,
        maximumAge: step.maximumAge,
      });
      attempts.push('browser-initial');
      source = 'browser-initial';
      break;
    } catch (error) {
      const err = error as Error & { code?: number };
      lastReason = err.message || 'Geolocation error';
      // Hard deny: stop immediately so we can surface site-settings help.
      if (err.code === 1 || isGeolocationPermissionDeniedReason(lastReason)) {
        return {
          fix: null,
          fulfilled: false,
          usedFallback: false,
          reason: lastReason,
          attempts,
        };
      }
      // Timeout / unavailable → try next ladder step.
    }
  }

  if (!position) {
    return {
      fix: null,
      fulfilled: false,
      usedFallback: false,
      reason: lastReason ?? 'Unable to acquire location',
      attempts,
    };
  }

  const accuracy = position.coords.accuracy ?? Number.POSITIVE_INFINITY;
  if (accuracy <= desiredAccuracy) {
    const fix = toFix(position, source);
    recordFix(fix);
    return {
      fix,
      fulfilled: true,
      usedFallback: false,
      attempts,
    };
  }

  // Desktop Safari / Chrome Wi‑Fi first fixes are often ±hundreds of meters to
  // kilometers and never tighten via watchPosition — return immediately.
  if (accuracy > COARSE_SKIP_REFINE_METERS) {
    const fix = toFix(position, source);
    recordFix(fix);
    return {
      fix,
      fulfilled: false,
      usedFallback: false,
      reason: 'Coarse browser/Wi-Fi location (skipped refine)',
      attempts,
    };
  }

  // Mid-range accuracy: sample briefly for a tighter outdoor fix.
  return await new Promise((resolve) => {
    let resolved = false;
    let watchId: number | null = null;
    let refinementTimer: number | null = null;
    let bestPosition: GeolocationPosition = position!;
    let bestSource: LocationSource = source;

    const cleanup = () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
      if (refinementTimer !== null) {
        window.clearTimeout(refinementTimer);
        refinementTimer = null;
      }
    };

    const finish = (pos: GeolocationPosition, src: LocationSource, reason?: string) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      const fix = toFix(pos, src);
      recordFix(fix);
      const nextAttempts =
        src === 'browser-refined' || src === 'browser-watch'
          ? [...attempts, src]
          : attempts;
      resolve({
        fix,
        fulfilled: (fix.accuracy ?? Number.POSITIVE_INFINITY) <= desiredAccuracy,
        usedFallback: false,
        reason,
        attempts: nextAttempts,
      });
    };

    watchId = navigator.geolocation.watchPosition(
      (watchPosition) => {
        const watchAccuracy = watchPosition.coords.accuracy ?? Number.POSITIVE_INFINITY;
        const bestAccuracy = bestPosition.coords.accuracy ?? Number.POSITIVE_INFINITY;
        if (watchAccuracy < bestAccuracy) {
          bestPosition = watchPosition;
          bestSource = 'browser-refined';
        }
        if (watchAccuracy <= desiredAccuracy) {
          finish(watchPosition, 'browser-refined');
        }
      },
      () => {
        // Ignore watch errors; refinement timer returns best so far.
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        // Per-update timeout: Safari fires this often on Wi‑Fi; keep generous.
        timeout: Math.max(refinementTimeout, 15000),
      },
    );

    refinementTimer = window.setTimeout(() => {
      finish(bestPosition, bestSource, 'Refinement timed out');
    }, refinementTimeout);
  });
}

async function attemptHostLocation(): Promise<{ fix: LocationFix | null; reason?: string }> {
  try {
    const position = await hostWebApp.requestLocation();
    if (!position) {
      return { fix: null, reason: 'Host bridge returned no location' };
    }
    const fix: LocationFix = {
      lat: position.latitude,
      lng: position.longitude,
      accuracy: null,
      timestamp: Date.now(),
      source: 'hostWebApp',
    };
    recordFix(fix);
    return { fix };
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    return { fix: null, reason };
  }
}

export async function requestPreciseLocation(
  options: HighAccuracyOptions = {}
): Promise<HighAccuracyResult> {
  const desiredAccuracy = options.desiredAccuracyMeters ?? DEFAULT_DESIRED_ACCURACY;
  const initialTimeout = options.initialTimeoutMs ?? DEFAULT_INITIAL_TIMEOUT;
  const refinementTimeout = options.refinementTimeoutMs ?? DEFAULT_REFINEMENT_TIMEOUT;
  const allowHostLocation = options.allowHostLocationFallback ?? false;
  const allowLastKnown = options.allowLastKnownFallback ?? true;
  const maxLastKnownAge = options.maxLastKnownAgeMs ?? DEFAULT_MAX_LAST_KNOWN_AGE;

  const browserResult = await attemptBrowserLocation(
    desiredAccuracy,
    initialTimeout,
    refinementTimeout
  );

  if (browserResult.fix) {
    return browserResult;
  }

  let attempts: LocationSource[] = [...browserResult.attempts];
  let reason = browserResult.reason;

  if (allowHostLocation) {
    const hostLocationAttempt = await attemptHostLocation();
    if (hostLocationAttempt.fix) {
      return {
        fix: hostLocationAttempt.fix,
        fulfilled: false,
        usedFallback: true,
        reason: 'Using host-provided location. Accuracy unknown.',
        attempts: [...attempts, 'hostWebApp'],
      };
    }
    reason = hostLocationAttempt.reason ?? reason;
    attempts = [...attempts, 'hostWebApp'];
  }

  if (allowLastKnown) {
    const lastKnown = getLastKnownFix(maxLastKnownAge);
    if (lastKnown) {
      return {
        fix: lastKnown,
        fulfilled: (lastKnown.accuracy ?? Number.POSITIVE_INFINITY) <= desiredAccuracy,
        usedFallback: true,
        reason: reason ?? 'Using last known location',
        attempts: [...attempts, 'last-known'],
      };
    }
  }

  return {
    fix: null,
    fulfilled: false,
    usedFallback: false,
    reason: reason ?? 'Unable to acquire location',
    attempts,
  };
}

export function storeExternalFix(fix: LocationFix) {
  recordFix(fix);
}

/** Pin drops (poop / Flock / taco / cameras) must be phone GPS, not Telegram/IP civic POI. */
export const REPORT_PIN_MAX_ACCURACY_METERS = 100;
export const REPORT_PIN_COARSE_GPS_MESSAGE =
  'GPS is too coarse to drop a pin. Turn on location and try again.';

export function isReportGradeFix(fix: LocationFix | null | undefined): fix is LocationFix {
  if (!fix) return false;
  if (fix.source === 'hostWebApp') return false;
  if (fix.accuracy == null || !Number.isFinite(fix.accuracy)) return false;
  if (fix.accuracy > REPORT_PIN_MAX_ACCURACY_METERS) return false;
  return Number.isFinite(fix.lat) && Number.isFinite(fix.lng);
}

export function filterReportGradeResult(result: HighAccuracyResult): HighAccuracyResult {
  if (result.fix && !isReportGradeFix(result.fix)) {
    return {
      ...result,
      fix: null,
      fulfilled: false,
      reason: REPORT_PIN_COARSE_GPS_MESSAGE,
    };
  }
  return result;
}

/** Browser high-accuracy only. Rejects Telegram/host IP location and fixes worse than 100 m. */
export async function requestReportPinLocation(
  options: Omit<HighAccuracyOptions, 'allowHostLocationFallback'> = {},
): Promise<HighAccuracyResult> {
  const result = await requestPreciseLocation({
    desiredAccuracyMeters: options.desiredAccuracyMeters ?? 20,
    initialTimeoutMs: options.initialTimeoutMs,
    refinementTimeoutMs: options.refinementTimeoutMs ?? 7000,
    allowLastKnownFallback: options.allowLastKnownFallback ?? true,
    maxLastKnownAgeMs: options.maxLastKnownAgeMs ?? 45000,
    allowHostLocationFallback: false,
  });
  return filterReportGradeResult(result);
}
